import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import { TvDashboard, TvDevice } from 'src/api/zrm';
import { dashboardLabel, formatCountdown, formatPairingCode, pairingDeadline, PAIRING_TTL_MS } from 'src/tv/pairingCode';
import { pollTvPairing, retryDelayMs, startTvPairing, storeTvToken } from 'src/tv/tvClient';
import TvFrame from 'src/tv/TvFrame';
import { TV_COLORS } from 'src/tv/tvTheme';
import httpStatusOf from 'src/utils/httpStatusOf';

const POLL_INTERVAL_MS = 3_000;

const codeArrives = keyframes`
  from { opacity: 0; filter: blur(0.6vw); transform: translateY(1vh); }
  to { opacity: 1; filter: blur(0); transform: none; }
`;

interface ActivePairing {
  code: string;
  deadline: number;
}

interface TvPairingScreenProps {
  dashboard: TvDashboard;
  /** Gets the token too: storage may be blocked, so the caller keeps it in memory. */
  onPaired: (device: TvDevice, token: string) => void;
}

/**
 * Shows a one-time code (and a QR to the ZRM claim page) until an agent claims it. Starts a new code
 * whenever the server says the current one is gone (410), and backs off on any failure — a screen
 * nobody is watching must never hammer the API.
 */
const TvPairingScreen: FC<TvPairingScreenProps> = (props) => {
  const { dashboard, onPaired } = props;
  const { t } = useTranslation();

  const [pairing, setPairing] = useState<ActivePairing | null>(null);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1_000);

    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    let isCancelled = false;
    let timer: number | undefined;
    let failures = 0;
    let pairingId = '';
    let pollSecret = '';

    const schedule = (step: () => Promise<void>, delayMs: number) => {
      timer = window.setTimeout(step, delayMs);
    };

    const retryLater = (step: () => Promise<void>) => {
      const delayMs = retryDelayMs(failures);

      failures += 1;
      setRetryAt(Date.now() + delayMs);
      schedule(step, delayMs);
    };

    const poll = async () => {
      try {
        const result = await pollTvPairing(pairingId, pollSecret);

        if (isCancelled) return;

        setRetryAt(null);

        if (result.status === 'paired') {
          storeTvToken(dashboard, result.token);
          onPaired(result.device, result.token);

          return;
        }

        failures = 0;
        schedule(poll, POLL_INTERVAL_MS);
      } catch (error) {
        if (isCancelled) return;

        if (httpStatusOf(error) === 410) {
          // Expired (or used): fetch a fresh code. A pairing that never got a `pending` answer
          // keeps `failures` up, so a server that answers 410 at once still backs off.
          setPairing(null);
          schedule(start, retryDelayMs(failures));
          failures += 1;

          return;
        }

        retryLater(poll);
      }
    };

    const start = async () => {
      try {
        const started = await startTvPairing(dashboard);

        if (isCancelled) return;

        pairingId = started.pairing_id;
        pollSecret = started.poll_secret;
        setRetryAt(null);
        setPairing({ code: started.code, deadline: pairingDeadline(started.expires_at, Date.now()) });
        schedule(poll, POLL_INTERVAL_MS);
      } catch {
        if (isCancelled) return;

        retryLater(start);
      }
    };

    start();

    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [dashboard, onPaired]);

  const remainingMs = pairing ? Math.max(0, pairing.deadline - now) : 0;
  const claimUrl = pairing ? `${window.location.origin}/tv-screens?code=${pairing.code}` : '';
  const [first, second, third] = pairing ? formatPairingCode(pairing.code).split('-') : [];

  return (
    <TvFrame
      aside={(
        <Typography sx={{ fontSize: '1.6vw', color: TV_COLORS.muted, fontWeight: 500 }}>
          {dashboardLabel(t, dashboard)}
        </Typography>
      )}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '6vw' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{ fontSize: '3.4vw', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em' }}
            >
              {t('Connect this screen')}
            </Typography>
            <Typography sx={{ mt: '1.6vh', fontSize: '1.7vw', color: TV_COLORS.muted, lineHeight: 1.4, maxWidth: '40ch' }}>
              {t('In ZRM, open TV screens and enter this code. Or scan the QR code with your phone.')}
            </Typography>
          </Box>

          <Box
            role="img"
            aria-label={t('Scan to connect this screen')}
            data-testid="tv-pairing-qr"
            sx={{
              flexShrink: 0,
              p: '1vw',
              borderRadius: '1vw',
              bgcolor: TV_COLORS.text,
              width: 'min(28vh, 18vw)',
              aspectRatio: '1',
              opacity: pairing ? 1 : 0.08,
              transition: 'opacity 400ms ease-out',
            }}
          >
            {pairing && (
              <QRCode
                value={claimUrl}
                size={512}
                level="M"
                bgColor={TV_COLORS.text}
                fgColor={TV_COLORS.background}
                style={{ width: '100%', height: '100%', display: 'block' }}
              />
            )}
          </Box>
        </Box>

        <Box
          aria-live="polite"
          sx={{ mt: '6vh', height: 'min(10vw, 20vh)', display: 'flex', alignItems: 'center' }}
        >
          {pairing ? (
            <Typography
              key={pairing.code}
              aria-label={pairing.code.split('').join(' ')}
              data-testid="tv-pairing-code-display"
              sx={{
                fontSize: 'min(10vw, 20vh)',
                fontWeight: 600,
                lineHeight: 1,
                letterSpacing: '0.04em',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                animation: `${codeArrives} 700ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            >
              {first}
              <Box
                component="span"
                sx={{ color: TV_COLORS.accent, mx: '0.1em' }}
              >
                –
              </Box>
              {second}
              <Box
                component="span"
                sx={{ color: TV_COLORS.accent, mx: '0.1em' }}
              >
                –
              </Box>
              {third}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '2.4vw', color: TV_COLORS.muted }}>
              {t('Getting a code…')}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: '5vh' }}>
          <Box sx={{ height: '0.5vh', borderRadius: '0.25vh', bgcolor: TV_COLORS.line, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                bgcolor: TV_COLORS.accent,
                transform: `scaleX(${remainingMs / PAIRING_TTL_MS})`,
                transformOrigin: 'left',
                transition: 'transform 1s linear',
              }}
            />
          </Box>
          <Box sx={{ mt: '1.6vh', display: 'flex', justifyContent: 'space-between', gap: '4vw', fontSize: '1.5vw' }}>
            <Typography sx={{ fontSize: 'inherit', color: TV_COLORS.muted, fontVariantNumeric: 'tabular-nums' }}>
              {pairing ? t('A new code in {{time}}', { time: formatCountdown(remainingMs) }) : '\u00a0'}
            </Typography>
            {retryAt && (
              <Typography sx={{ fontSize: 'inherit', color: TV_COLORS.warning }}>
                {t('Cannot reach ZRM. Trying again in {{seconds}} s.', { seconds: Math.max(0, Math.ceil((retryAt - now) / 1000)) })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </TvFrame>
  );
};

export default TvPairingScreen;
